'use client';
import React, { useCallback, useState, useRef } from 'react';
import Image from 'next/image';
import { AnimatePresence, motion } from 'framer-motion';
import GradientCTA from '../gradientCta/GradientCta';
import CardBody from './CardBody';
import { useIsMobile } from '@/hooks/useIsMobile';
import classNames from 'classnames';

interface StiInfoCard {
  id: number;
  title: string;
  body: React.ReactElement;
}

interface StiOption {
  name: string;
  stiInfoCards: StiInfoCard[];
  isComingSoon: boolean;
}

interface StiInfoClientProps {
  stiOptions: StiOption[];
}

export default function StiInfoClient({
  stiOptions,
}: StiInfoClientProps): React.ReactElement {
  const [selectedSti, _setSelectedSti] = React.useState<StiOption>(
    stiOptions[0]
  );
  const [selectedInfoCard, setSelectedInfoCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const setSelectedSti = useCallback((stiOption: StiOption) => {
    _setSelectedSti(stiOption);
    setSelectedInfoCard(null);
  }, []);

  const getBaseCardClass = (): string => {
    return `bg-linear-to-t from-[#d64718] to-[#e98c6c] rounded-[10px] text-white flex flex-col justify-between items-start min-h-[314px] h-full opacity-85 max-[768px]:flex-row max-[768px]:justify-between max-[640px]:min-h-[160px] max-[768px]:min-h-auto max-[768px]:w-full`;
  };

  const isMobile = useIsMobile();

  return (
    <>
      <div className="flex h-full flex-col">
        <section
          data-component-name="sti-client-info"
          className="page-top-spacing-content mt-8 flex w-full flex-col py-[48px] md:py-[24px]"
        >
          <div className="content-wrapper w-full">
            <div>
              <h1 className="text-[44px]">STI Info</h1>
              <p className="mt-5 w-[70%] font-[Spectral] text-[30px] font-medium">
                Regardless of whether you covered it in health class, here is
                the information about STIs that you gotta know.
              </p>
            </div>

            <Image
              className="mt-8 sm:mt-16"
              src="/stiInfoBanner.png"
              height={300}
              width={1200}
              alt=""
            />
          </div>
        </section>

        <section
          ref={sectionRef}
          className="flex w-full flex-col py-[48px] md:py-[24px]"
        >
          <div className="content-wrapper flex w-full items-center justify-center gap-6 max-[640px]:flex-wrap max-[640px]:content-between max-[640px]:justify-evenly max-[640px]:gap-3">
            {stiOptions.map((stiOption, i) => (
              <motion.div
                key={`${stiOption.name}-${i}`}
                className={classNames(
                  'md:font-variant-small-caps flex min-w-[120px] cursor-pointer flex-col items-center text-center font-["Work_Sans"] text-sm leading-[23px] font-normal tracking-normal md:text-xs md:leading-[25px] md:font-medium md:tracking-[0.02em] md:uppercase',
                  stiOption.name === selectedSti.name
                    ? 'text-[#d64718]'
                    : 'text-[#ffac8c] opacity-30'
                )}
                onClick={() => {
                  setSelectedSti(stiOption);
                }}
              >
                <motion.div
                  className={classNames(
                    'mb-3 h-3 w-full min-w-[120px] rounded-[116px]',
                    stiOption.name === selectedSti.name
                      ? 'bg-linear-to-t from-[#d64718] to-[#e98c6c]'
                      : 'bg-linear-to-t from-[#e88864] to-[#ffac8c]'
                  )}
                  layout
                  transition={{ duration: 0.8, ease: 'easeInOut' }}
                />
                <span>{stiOption.name}</span>
              </motion.div>
            ))}
          </div>

          {selectedSti.isComingSoon ? (
            <div className="content-wrapper w-full">
              <motion.div
                className="mt-24 flex min-h-[367px] w-full flex-col justify-between rounded-[10px] bg-linear-to-t from-[#e88864] to-[#ffac8c] p-6 text-white opacity-80"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                {/* Figure out why next js isn't letting me add a link break in text */}
                <h3>Coming Soon.</h3>

                <span>Test for this condition coming soon</span>
              </motion.div>
            </div>
          ) : (
            <>
              <div className="mt-24 mb-2 flex items-center justify-center">
                <motion.div
                  key={selectedSti.name} // Key ensures re-animation on change
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.6, ease: 'easeInOut' }}
                  className="mb-4 text-center"
                >
                  <h3 className="font-work-sans text-xl leading-6 font-normal tracking-[-0.05em] text-[#d64718] md:text-2xl md:leading-8">
                    {selectedSti.name}
                  </h3>
                </motion.div>
              </div>
              <div className="content-wrapper w-full">
                <motion.div
                  className="flex w-full flex-row items-center justify-center gap-3 max-[1024px]:mt-12 max-[1024px]:flex-col md:gap-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <AnimatePresence mode="wait">
                    {selectedSti.stiInfoCards.map((card, index) => {
                      const isSelected = selectedInfoCard === card.id;
                      const isHidden =
                        selectedInfoCard && selectedInfoCard !== card.id;

                      return (
                        <motion.div
                          key={`${selectedSti.name}-${card.id}`}
                          className={classNames(
                            getBaseCardClass(),
                            'transition-[padding]',
                            isSelected
                              ? 'px-4 py-5 sm:px-6 sm:py-5 md:px-8 md:py-6'
                              : 'px-4 py-4 sm:px-3 sm:py-4'
                          )}
                          style={{
                            display: isHidden && isMobile ? 'none' : 'flex',
                            position: 'relative',
                            transformOrigin: 'center',
                            zIndex: isSelected ? 10 : 1,
                          }}
                          initial={{
                            opacity: 0,
                            width: '100%',
                            y: 20,
                          }}
                          animate={{
                            opacity: isHidden ? 0 : 1,
                            width: '100%',
                            x: isHidden ? (index < 2 ? -20 : 20) : 0,
                            y: 0,
                          }}
                          exit={{
                            opacity: 0,
                            transition: {
                              delay: (3 - index) * 0.1, // Cascading fade-out delay (reverse order)
                              duration: 0.6,
                              ease: [0.4, 0, 0.2, 1],
                            },
                            y: -20,
                          }}
                          transition={{
                            delay: isSelected
                              ? 0
                              : isHidden
                                ? (3 - index) * 0.05
                                : index * 0.15, // Increased delay for cascading effect
                            duration: 1.4,
                            ease: [0.4, 0, 0.2, 1],
                            opacity: {
                              delay: index * 0.15, // Cascading fade-in delay
                              duration: 0.6,
                              ease: [0.4, 0, 0.2, 1],
                            },
                            y: {
                              delay: index * 0.15, // Cascading slide-up delay
                              duration: 0.6,
                              ease: [0.4, 0, 0.2, 1],
                            },
                          }}
                        >
                          <motion.div>
                            <motion.div
                              className="overflow-hidden"
                              animate={{
                                opacity: selectedInfoCard === card.id ? 0 : 1,
                              }}
                              transition={{
                                delay: selectedInfoCard === card.id ? 0 : 0,
                                duration: 0.8,
                                ease: [0.4, 0, 0.2, 1],
                              }}
                            >
                              <p className="mb-2 font-['Work_Sans'] text-[44px] leading-[60px] font-normal tracking-[-0.05em] max-[640px]:text-2xl max-[640px]:leading-7 md:text-[44px] md:leading-[60px]">
                                {card.id}.
                              </p>
                            </motion.div>
                            <motion.h3
                              className="font-['Work_Sans'] text-xl leading-6 font-medium max-[640px]:text-lg max-[640px]:leading-6"
                              initial={{
                                fontSize: '1.25rem',
                                marginBottom: '0.5rem',
                              }}
                              animate={{
                                fontSize:
                                  selectedInfoCard === card.id
                                    ? '1.5rem'
                                    : '1.25rem',
                                marginBottom:
                                  selectedInfoCard === card.id
                                    ? '1rem'
                                    : '0.5rem',
                              }}
                              transition={{
                                delay: selectedInfoCard === card.id ? 0.4 : 0,
                                duration: 0.8,
                                ease: [0.4, 0, 0.2, 1],
                              }}
                            >
                              {card.title}
                            </motion.h3>
                            <CardBody
                              selectedInfoCard={selectedInfoCard}
                              cardId={card.id}
                              cardBody={card.body}
                            />
                          </motion.div>

                          <motion.div
                            className="flex"
                            animate={{
                              justifyContent:
                                selectedInfoCard === card.id
                                  ? 'flex-start'
                                  : isMobile
                                    ? 'flex-start'
                                    : 'center',
                            }}
                            transition={{
                              duration: 0.8,
                              ease: [0.4, 0, 0.2, 1],
                            }}
                          >
                            <motion.button
                              className="flex w-fit items-center justify-center rounded-[10px] bg-white p-3 text-center align-middle font-['Work_Sans'] text-base font-medium tracking-[-0.02em] text-black max-[640px]:mt-3 md:w-[140px] lg:w-[180px]"
                              onClick={() => {
                                const isClosing = selectedInfoCard === card.id;
                                setSelectedInfoCard(
                                  selectedInfoCard ? null : card.id
                                );

                                // Scroll to top of section when closing
                                if (isClosing && sectionRef.current) {
                                  sectionRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                  });
                                }
                              }}
                              animate={{
                                backgroundColor:
                                  selectedInfoCard === card.id
                                    ? '#f8f9fa'
                                    : '#ffffff',
                              }}
                              transition={{ duration: 0.3 }}
                            >
                              <span>
                                {selectedInfoCard === card.id
                                  ? 'Close'
                                  : 'View'}
                              </span>
                            </motion.button>
                          </motion.div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </motion.div>
              </div>
            </>
          )}
        </section>

        <GradientCTA heading={'Need help?'} buttonLink="/sign-in" />
      </div>
    </>
  );
}
